// Theme toggle script for Harvest Market
//
// This module controls switching between dark and light modes on the
// storefront.  It toggles `theme-dark` and `theme-light` classes on the
// `<html>` element and applies an alias `light-theme` on `<body>` for
// compatibility with legacy CSS selectors.  The user’s selection is
// persisted in localStorage under the key `hm-theme`.  Only a button with
// id `theme-toggle` and (optionally) a span with id `theme-icon` are
// required in your markup.
//
(function () {
  'use strict';

  const STORAGE_KEY = 'hm-theme';
  const root = document.documentElement;
  const body = document.body;

  /**
   * Apply the specified theme to the document.
   *
   * @param {'dark'|'light'} mode The theme to apply.
   */
  function applyTheme(mode) {
    const isDark = mode === 'dark';
    // Update classes on <html>
    root.classList.toggle('theme-dark', isDark);
    root.classList.toggle('theme-light', !isDark);
    // Provide alias on <body> for CSS selectors targeting body.light-theme
    body.classList.toggle('light-theme', !isDark);
    // Persist the choice
    try {
      localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
    } catch (err) {
      // localStorage may be unavailable; ignore errors
    }
    // Update the emoji icon if present
    const icon = document.getElementById('theme-icon');
    if (icon) {
      icon.textContent = isDark ? '🌙' : '☀️';
    }
    // If there are separate sun/moon icons, hide the inactive one
    const sun = document.getElementById('theme-icon-sun');
    const moon = document.getElementById('theme-icon-moon');
    if (sun && moon) {
      sun.style.display = isDark ? 'none' : 'inline-block';
      moon.style.display = isDark ? 'inline-block' : 'none';
    }
  }

  /**
   * Determine which theme should be applied on initial load.
   *
   * The function first tries to read a saved value from localStorage.  If
   * none exists, it falls back to the user’s OS preference via
   * `prefers-color-scheme`.  If that is unavailable, it defaults to
   * light mode.
   *
   * @returns {'dark'|'light'} The initial theme.
   */
  function getInitialTheme() {
    // 1. Saved preference
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') {
        return saved;
      }
    } catch (err) {
      // ignore errors when accessing localStorage
    }
    // 2. OS preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    // 3. Default to light
    return 'light';
  }

  /**
   * Bind the click handler to the theme toggle button.  If the button
   * doesn’t exist, this function does nothing.
   */
  function bindToggleHandler() {
    // Support either `theme-toggle` (preferred) or `theme-toggle-btn` as the
    // trigger element.  Some versions of the theme may name the button
    // `theme-toggle-btn`.  If neither is found, no handler is bound.
    const toggleBtn =
      document.getElementById('theme-toggle') ||
      document.getElementById('theme-toggle-btn');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isCurrentlyDark = root.classList.contains('theme-dark');
        applyTheme(isCurrentlyDark ? 'light' : 'dark');
      });
    }
  }

  // Immediately apply the initial theme to prevent a flash of the wrong
  // colors.  Do this synchronously so the classes are in place before
  // the page paints.
  applyTheme(getInitialTheme());
  // Add smooth transitions only after the first paint to avoid animating
  // the initial load.
  body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
  // Bind the toggle button after the DOM is ready.  If the DOM is already
  // parsed when this script runs, call the function immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindToggleHandler);
  } else {
    bindToggleHandler();
  }
})();